import type { BrowserOptions } from "@sentry/nextjs";
import { isExpectedNoiseError } from "@sum-video/shared/utils/expectedTrpcErrors";
import { SentryApp, SentryTag } from "@/lib/sentry/tags";

export const SENTRY_DSN =
  process.env.NEXT_PUBLIC_SENTRY_DSN ??
  "https://d8bbf92b1e33f0d95ddf183b1ae72e97@o4508242157109248.ingest.de.sentry.io/4510217243656272";

export function getTracesSampleRate(): number {
  return process.env.NODE_ENV === "development" ? 1 : 0.1;
}

export function isSentryForceEnabled(): boolean {
  return process.env.NEXT_PUBLIC_SENTRY_ENABLED === "true";
}

export function isSentryEnabledOnServer(): boolean {
  if (isSentryForceEnabled()) {
    return true;
  }
  return process.env.NODE_ENV !== "development";
}

export function isSentryEnabledInBrowser(): boolean {
  if (typeof window === "undefined") {
    return isSentryEnabledOnServer();
  }
  if (isSentryForceEnabled()) {
    return true;
  }
  const hostname = window.location.hostname;
  return (
    hostname !== "localhost" &&
    hostname !== "127.0.0.1" &&
    hostname !== "0.0.0.0"
  );
}

/** The one set of options the browser SDK is initialized with. */
export function browserSentryOptions(): BrowserOptions {
  return {
    dsn: SENTRY_DSN,
    tracesSampleRate: getTracesSampleRate(),
    enableLogs: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,
    initialScope: {
      tags: {
        [SentryTag.App]: SentryApp.Web,
      },
    },
    beforeSend(event, hint) {
      if (isExpectedNoiseError(hint.originalException)) {
        return null;
      }
      const values = event.exception?.values ?? [];
      for (const value of values) {
        if (value.value && isExpectedNoiseError(new Error(value.value))) {
          return null;
        }
        if (value.type && isExpectedNoiseError(new Error(value.type))) {
          return null;
        }
      }
      return event;
    },
  };
}
